import { io, Socket } from 'socket.io-client';
import authService from './authService';
import { isAutomatedAuditRuntime } from '@/lib/auditDetection';

// Đồng bộ fallback URL với next.config để tránh dùng localhost trong production
const SOCKET_BASE_URL = process.env.NEXT_PUBLIC_SOCKET_URL || (
  process.env.NODE_ENV === 'production'
    ? 'https://sdtc.vn'
    : 'http://localhost:7001'
);
const SOCKET_PATH = process.env.NEXT_PUBLIC_SOCKET_PATH || '/api/socket.io';

// Kiểm tra xem có thể kết nối socket không (chỉ trong browser và khi có URL hợp lệ)
const canConnectSocket = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isAutomatedAuditRuntime()) return false;
  
  // Kiểm tra URL hợp lệ
  if (!SOCKET_BASE_URL) return false;
  
  // Kiểm tra URL có protocol hợp lệ không
  try {
    const url = new URL(SOCKET_BASE_URL);
    const isProd = process.env.NODE_ENV === 'production';
    const isLocalHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(url.hostname);
    const isBrowserLocal = ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
    // Chỉ cho phép http/https
    if (!['http:', 'https:'].includes(url.protocol)) {
      return false;
    }
    
    // Trong production, chặn localhost trừ khi đang chạy trên localhost (dev build chạy local) 
    if (isProd && isLocalHost && !isBrowserLocal) {
      return false;
    }
    
    // Kiểm tra hostname có hợp lệ không (không phải empty)
    if (!url.hostname || url.hostname.trim() === '') {
      return false;
    }
  } catch {
    return false;
  }
  
  return true;
};

// Suppress WebSocket errors globally trong production (chỉ cho socket.io errors)
// Chỉ áp dụng một lần để tránh override nhiều lần
if (typeof window !== 'undefined' && 
    !(window as any).__socketErrorSuppressed) {
  (window as any).__socketErrorSuppressed = true;
  const originalError = window.console.error;
  const originalWarn = window.console.warn;
  
  window.console.error = (...args: any[]) => {
    // Suppress WebSocket connection errors từ socket.io và engine.io-client
    const errorString = String(args[0] || '');
    const fullMessage = args.map(a => String(a)).join(' ');
    
    // Bắt mọi lỗi WebSocket liên quan đến socket.io
    if ((errorString.includes('WebSocket') || fullMessage.includes('WebSocket')) && 
        (errorString.includes('socket.io') || 
         fullMessage.includes('socket.io') ||
         errorString.includes('engine.io') ||
         fullMessage.includes('engine.io') ||
         errorString.includes('ERR_NAME_NOT_RESOLVED') ||
         fullMessage.includes('ERR_NAME_NOT_RESOLVED') ||
         errorString.includes('connection establishment') ||
         fullMessage.includes('connection establishment') ||
         errorString.includes('wss://sdtc.vn') ||
         fullMessage.includes('wss://sdtc.vn') ||
         errorString.includes('ws://') ||
         fullMessage.includes('ws://') ||
         errorString.includes('net::ERR') ||
         fullMessage.includes('net::ERR'))) {
      return; // Suppress error để không ảnh hưởng PageSpeed
    }
    originalError.apply(console, args);
  };
  
  window.console.warn = (...args: any[]) => {
    // Suppress WebSocket warnings từ socket.io và engine.io-client
    const warnString = String(args[0] || '');
    const fullMessage = args.map(a => String(a)).join(' ');
    
    // Bắt mọi warning WebSocket liên quan đến socket.io
    if ((warnString.includes('WebSocket') || fullMessage.includes('WebSocket')) && 
        (warnString.includes('socket.io') || 
         fullMessage.includes('socket.io') ||
         warnString.includes('engine.io') ||
         fullMessage.includes('engine.io') ||
         warnString.includes('ERR_NAME_NOT_RESOLVED') ||
         fullMessage.includes('ERR_NAME_NOT_RESOLVED') ||
         warnString.includes('connection establishment') ||
         fullMessage.includes('connection establishment') ||
         warnString.includes('wss://sdtc.vn') ||
         fullMessage.includes('wss://sdtc.vn') ||
         warnString.includes('ws://') ||
         fullMessage.includes('ws://'))) {
      return; // Suppress warning để không ảnh hưởng PageSpeed
    }
    originalWarn.apply(console, args);
  };
}

let userSocket: Socket | null = null;
let adminSocket: Socket | null = null;
let connectionErrorLogged = false; // Flag để tránh spam log
let reconnectAttempts = 0; // Đếm số lần retry
const MAX_RECONNECT_ATTEMPTS = 3; // Giới hạn số lần retry

// Suppress console errors trong production
const suppressConsoleError = (error: any) => {
  if (process.env.NODE_ENV === 'production') {
    // Trong production, không log lỗi ra console để tránh ảnh hưởng PageSpeed
    return;
  }
  // Chỉ log trong development
  console.warn('Socket.IO connection error:', error.message);
};

export function getUserSocket(options?: {
  sessionId?: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  autoConnect?: boolean; // Cho phép điều khiển khi nào kết nối
}): Socket {
  // Wrapper để bắt mọi lỗi và trả về mock socket nếu có lỗi
  try {
  // Nếu socket đã tồn tại
  if (userSocket) {
    // Nếu đang kết nối hoặc đang trong quá trình kết nối, chỉ cập nhật auth
    if (userSocket.connected || userSocket.active) {
      // Cập nhật auth nếu có thay đổi
      if (options?.sessionId || options?.userName || options?.userEmail || options?.userPhone) {
        userSocket.auth = {
          sessionId: options?.sessionId,
          userName: options?.userName,
          userEmail: options?.userEmail,
          userPhone: options?.userPhone,
        };
      }
      return userSocket;
    }
    
    // Nếu socket đã disconnect và đã vượt quá số lần retry, tắt reconnection
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      // Giữ socket nhưng tắt reconnection để tránh spam
      userSocket.io.opts.reconnection = false;
      return userSocket;
    }
    
    // Nếu socket tồn tại nhưng chưa kết nối và chưa vượt quá limit, giữ nguyên socket
    // Chỉ cập nhật auth và bật reconnection nếu cần
    const shouldAutoConnect = options?.autoConnect !== false;
    if (shouldAutoConnect && !userSocket.io.opts.reconnection) {
      userSocket.io.opts.reconnection = true;
      userSocket.io.opts.reconnectionAttempts = MAX_RECONNECT_ATTEMPTS - reconnectAttempts;
    }
    
    if (options?.sessionId || options?.userName || options?.userEmail || options?.userPhone) {
      userSocket.auth = {
        sessionId: options?.sessionId,
        userName: options?.userName,
        userEmail: options?.userEmail,
        userPhone: options?.userPhone,
      };
    }
    
    return userSocket;
  }

  const shouldAutoConnect = options?.autoConnect !== false && canConnectSocket(); // Chỉ kết nối nếu có thể

  // Chỉ tạo socket nếu có thể kết nối
  if (!canConnectSocket()) {
    // Trả về một mock socket object để tránh lỗi
    return {
      connected: false,
      active: false,
      disconnected: true,
      on: () => {},
      off: () => {},
      emit: () => {},
      disconnect: () => {},
      connect: () => {},
      io: {
        opts: {
          reconnection: false,
        },
      },
    } as any as Socket;
  }

  userSocket = io(SOCKET_BASE_URL, {
    path: SOCKET_PATH,
    transports: ['websocket', 'polling'],
    withCredentials: true,
    autoConnect: shouldAutoConnect,
    reconnection: shouldAutoConnect, // Chỉ retry nếu đã bật autoConnect
    reconnectionDelay: 3000, // Đợi 3 giây trước khi retry
    reconnectionDelayMax: 10000, // Tối đa 10 giây giữa các lần retry
    reconnectionAttempts: shouldAutoConnect ? MAX_RECONNECT_ATTEMPTS : 0, // Giới hạn số lần retry
    timeout: 10000, // Timeout 10 giây
    auth: {
      sessionId: options?.sessionId,
      userName: options?.userName,
      userEmail: options?.userEmail,
      userPhone: options?.userPhone,
    },
    // Suppress errors trong production
    forceNew: false,
  });

  // Reset reconnectAttempts khi kết nối thành công
  userSocket.on('connect', () => {
    connectionErrorLogged = false;
    reconnectAttempts = 0;
  });

  // Xử lý lỗi kết nối - suppress errors trong production
  userSocket.on('connect_error', (error) => {
    reconnectAttempts++;
    
    // Tắt reconnection sau khi đã thử quá nhiều lần
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (userSocket) {
        userSocket.io.opts.reconnection = false;
        // Disconnect để tránh tiếp tục cố gắng kết nối
        try {
          userSocket.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
      }
      if (!connectionErrorLogged) {
        suppressConsoleError(error);
        connectionErrorLogged = true;
      }
    } else if (!connectionErrorLogged) {
      suppressConsoleError(error);
      connectionErrorLogged = true;
      // Reset flag sau 30 giây để có thể log lại nếu cần
      setTimeout(() => {
        connectionErrorLogged = false;
      }, 30000);
    }
  });

  // Reset reconnectAttempts khi disconnect
  userSocket.on('disconnect', () => {
    // Không reset reconnectAttempts ở đây vì có thể là disconnect tạm thời
  });

  return userSocket;
  } catch (error) {
    // Nếu có lỗi khi khởi tạo socket, trả về mock socket để tránh crash
    suppressConsoleError(error);
    return {
      connected: false,
      active: false,
      disconnected: true,
      on: () => {},
      off: () => {},
      emit: () => {},
      disconnect: () => {},
      connect: () => {},
      io: {
        opts: {
          reconnection: false,
        },
      },
    } as any as Socket;
  }
}

let adminConnectionErrorLogged = false; // Flag riêng cho admin socket
let adminReconnectAttempts = 0; // Đếm số lần retry cho admin socket

export function getAdminSocket(options?: { tokenOverride?: string }): Socket {
  const token = options?.tokenOverride ?? authService.getToken();

  if (!token) {
    throw new Error('Admin token is required to initialize socket');
  }

  // Nếu socket đã tồn tại
  if (adminSocket) {
    // Nếu đang kết nối hoặc đang trong quá trình kết nối, return ngay
    if (adminSocket.connected || adminSocket.active) {
      return adminSocket;
    }
    
    // Nếu socket đã disconnect và đã vượt quá số lần retry, tắt reconnection
    if (adminReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      adminSocket.io.opts.reconnection = false;
      return adminSocket;
    }
    
    // Cập nhật auth nếu token thay đổi
    const currentAuth = typeof adminSocket.auth === 'function' ? null : adminSocket.auth;
    if (!currentAuth || (currentAuth as any).token !== token) {
      adminSocket.auth = { token, isAdmin: true };
    }
    
    // Bật lại reconnection nếu cần
    if (!adminSocket.io.opts.reconnection) {
      adminSocket.io.opts.reconnection = true;
      adminSocket.io.opts.reconnectionAttempts = MAX_RECONNECT_ATTEMPTS - adminReconnectAttempts;
    }
    
    return adminSocket;
  }

  const shouldConnect = canConnectSocket();
  
  // Chỉ tạo socket nếu có thể kết nối
  if (!shouldConnect) {
    throw new Error('Cannot connect to socket: invalid configuration');
  }
  
  adminSocket = io(SOCKET_BASE_URL, {
    path: SOCKET_PATH,
    transports: ['websocket', 'polling'],
    withCredentials: true,
    autoConnect: shouldConnect,
    reconnection: shouldConnect,
    reconnectionDelay: 3000,
    reconnectionDelayMax: 10000,
    reconnectionAttempts: shouldConnect ? MAX_RECONNECT_ATTEMPTS : 0,
    timeout: 10000,
    auth: {
      token,
      isAdmin: true,
    },
    forceNew: false,
  });

  // Reset adminReconnectAttempts khi kết nối thành công
  adminSocket.on('connect', () => {
    adminConnectionErrorLogged = false;
    adminReconnectAttempts = 0;
  });

  // Xử lý lỗi kết nối cho admin socket - suppress errors trong production
  adminSocket.on('connect_error', (error) => {
    adminReconnectAttempts++;
    
    // Tắt reconnection sau khi đã thử quá nhiều lần
    if (adminReconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      if (adminSocket) {
        adminSocket.io.opts.reconnection = false;
        // Disconnect để tránh tiếp tục cố gắng kết nối
        try {
          adminSocket.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
      }
      if (!adminConnectionErrorLogged) {
        suppressConsoleError(error);
        adminConnectionErrorLogged = true;
      }
    } else if (!adminConnectionErrorLogged) {
      suppressConsoleError(error);
      adminConnectionErrorLogged = true;
      setTimeout(() => {
        adminConnectionErrorLogged = false;
      }, 30000);
    }
  });

  return adminSocket;
}

export function disconnectSockets(): void {
  if (userSocket) {
    userSocket.io.opts.reconnection = false; // Tắt reconnection trước khi disconnect
    userSocket.disconnect();
    userSocket = null;
    reconnectAttempts = 0; // Reset counter
  }
  if (adminSocket) {
    adminSocket.io.opts.reconnection = false; // Tắt reconnection trước khi disconnect
    adminSocket.disconnect();
    adminSocket = null;
    adminReconnectAttempts = 0; // Reset counter
  }
}


